import { Window } from '@execonline-inc/browser-window.private';
import { observer } from 'mobx-react';
import { emptyFragment } from '@execonline-inc/execonline-ui';
import { L, NotTranslated, T } from '../../../../../../../Translations';
import Modal from '../../../../../../Modal';
import { OpenableState } from '../../../../../../Modal/UseOpenable';
import { ProductLicenseOverview } from '../../../../Types';
import UsageBar from './UsageBar';
import { putQueryParam, toUrl } from '@execonline-inc/url';
import { currentUserStore } from '../../../../../../../CurrentUserStore';
import { findLink } from '../../../../../../../LinkyLinky';

interface Props {
  license: ProductLicenseOverview;
  modalState: OpenableState;
  close: () => void;
}

function LicenseUsageDetailsModal({ license, modalState, close }: Props) {
  const goToParticipants = (licenseStatus: string) => {
    toUrl('/people/participants')
      .map(putQueryParam('participants_filter[sort_direction]', 'desc'))
      .map(putQueryParam('participants_filter[sort_by]', 'last_sign_in_at'))
      .map(putQueryParam('participants_filter[license_status]', licenseStatus))
      .map(putQueryParam('participants_filter[license_id]', license.id.toString()))
      .map((url) => {
        Window.openWindow(url.href, 'participants_with_license');
      });
  };

  const exportCsv = () => {
    findLink('product-license-export', currentUserStore.links)
      .map((link) => link.href.replace('{product_license_id}', license.id.toString()))
      .map((url) => {
        Window.openWindow(url, '_blank');
      });
  };

  const labelFor = (license: ProductLicenseOverview) => {
    const targetId = `products-usage-modal-license-${license.id}-label`;

    return license.publicName
      .map((publicName) => (
        <span
          className="font-light text-sm bg-[#187a90] text-white px-2 py-1 mx-2 rounded-sm"
          data-intercom-target={targetId}
        >
          <NotTranslated text={publicName} />
        </span>
      ))
      .getOrElse(emptyFragment);
  };

  switch (modalState.kind) {
    case 'closed':
      return <></>;
    case 'open':
      return (
        <Modal close={close} title="">
          <div className="mb-8">
            <div className="font-medium text-2xl">
              <T kind="License Usage to Date" />
            </div>
          </div>
          <div className="text-xl mb-3">
            <NotTranslated text={license.name} />
            {labelFor(license)}
          </div>
          <div className="mb-5">
            <T
              kind="{{organizationName}} purchased {{count}} license..."
              organizationName={license.organizationName}
              count={license.licenseQuantity}
              date={<L localizeable={license.endOn} format="long-month-day-year" />}
            />{' '}
            (<T kind="{{count}} day remaining" count={license.daysRemaining} />)
          </div>
          <div className="w-full h-12 mb-5 border border-gray-300 overflow-clip">
            <UsageBar
              activated={license.activatedCount}
              allocated={license.allocatedCount}
              purchased={license.licenseQuantity}
            />
          </div>
          <div className="flex items-center my-5 gap-3">
            <div className="w-5 h-5 rounded-sm overflow-clip">
              <UsageBar activated={1} allocated={0} purchased={1} />
            </div>
            <div
              onClick={() => goToParticipants('Activated')}
              className="cursor-pointer text-teal-600"
            >
              {license.activatedCount} <T kind="Activated" />
            </div>
          </div>
          <div className="flex items-center my-5 gap-3">
            <div className="w-5 h-5 rounded-sm overflow-clip">
              <UsageBar activated={0} allocated={1} purchased={1} />
            </div>
            <div
              onClick={() => goToParticipants('Allocated')}
              className="cursor-pointer text-teal-600"
            >
              {license.allocatedCount} <T kind="Allocated" />
            </div>
          </div>
          <div className="flex items-center my-5 gap-3">
            <div className="w-5 h-5 border border-gray-300 rounded-sm overflow-clip">
              <UsageBar activated={0} allocated={1} purchased={2} />
            </div>
            <div>
              {license.activatableCount} <T kind="Available to Activate" />
            </div>
          </div>
          {(license.activatedCount > 0 || license.allocatedCount > 0) && (
            <div className="mt-8 flex justify-center">
              <button
                onClick={exportCsv}
                className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded font-medium"
              >
                <T kind="Download CSV" />
              </button>
            </div>
          )}
        </Modal>
      );
  }
}

export default observer(LicenseUsageDetailsModal);
